/* ============================================================================
 * workRunner — main-thread pool over the geometry + slicer Workers.
 * Static per-module Worker URLs (Vite bundles these; dynamic templates would
 * not be statically analyzable). Lazily spawns one Worker per kind, round-
 * robins task ids, resolves one Promise per task, falls back gracefully.
 * ========================================================================== */

const pools = new Map();      // kind -> Worker
const pending = new Map();      // id -> { resolve, reject, timeout }
let counter = 0;

const WORKER_URLS = {
    geometry: new URL('../workers/geometryWorker.js', import.meta.url),
    slicer: new URL('../workers/slicerWorker.js', import.meta.url),
};

function spawn(kind) {
    const worker = new Worker(WORKER_URLS[kind], { type: 'module' });
    worker.onmessage = (e) => {
        const p = pending.get(e.data.id);
        if (!p) return;
        clearTimeout(p.timeout);
        pending.delete(e.data.id);
        e.data.ok ? p.resolve(e.data.result) : p.reject(new Error(e.data.error || 'Worker task failed'));
    };
    worker.onerror = (err) => console.error(`[workRunner] ${kind} error:`, err.message);
    return worker;
}

function getPool(kind) {
    if (!pools.has(kind)) pools.set(kind, spawn(kind));
    return pools.get(kind);
}

export function runInWorker(kind, task, payload, timeoutMs = 20000) {
    if (typeof Worker === 'undefined') return Promise.reject(new Error('Web Workers unavailable here.'));
    return new Promise((resolve, reject) => {
        const id = ++counter;
        const worker = getPool(kind);
        const timeout = setTimeout(() => { pending.delete(id); reject(new Error(`Worker task "${task}" timed out.`)); }, timeoutMs);
        pending.set(id, { resolve, reject, timeout });
        worker.postMessage({ id, task, payload });
    });
}

export const runGeometryTask = (task, payload) => runInWorker('geometry', task, payload);
export const runSlicerTask = (task, payload) => runInWorker('slicer', task, payload);

/* Convenience wrapper used by the Boolean toolbar. */
export async function booleanInWorker(parts, op) {
    try {
        const result = await runGeometryTask('boolean', { parts, op });
        return Array.isArray(result) ? result : parts;
    } catch (err) {
        console.warn('[workRunner] boolean fell back to main thread:', err.message);
        return parts;
    }
}